import { Button } from '@/components/ui/Button';
import { useInvoices } from '@/hooks/useBilling';
import {
  formatAmount,
  formatBillingDate,
  getBillingStatusColor,
  translateBillingStatus,
} from '@/utils/billing.utils';
import { useTranslation } from 'react-i18next';
import {
  Download,
  FileText,
  Loader2,
  ExternalLink,
  Calendar,
} from 'lucide-react';
import { useState } from 'react';
import type { MonthlyInvoice } from '@/types/billing.types';
import { MonthlyBillingStatus } from '@/types/billing.types';

interface InvoicesListProps {
  onViewDetails: (invoiceUuid: string) => void;
}

export function InvoicesList({ onViewDetails }: InvoicesListProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<
    MonthlyBillingStatus | undefined
  >();
  const { data, isLoading, error } = useInvoices(page, 10, statusFilter);

  const invoices = data?.invoices || [];
  const pagination = data?.pagination;

  const handleDownloadInvoice = (invoice: MonthlyInvoice) => {
    if (invoice.stripeInvoicePdfUrl) {
      window.open(invoice.stripeInvoicePdfUrl, '_blank');
    } else if (invoice.stripeInvoiceUrl) {
      window.open(invoice.stripeInvoiceUrl, '_blank');
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          value={statusFilter || 'all'}
          onChange={e =>
            setStatusFilter(
              e.target.value === 'all'
                ? undefined
                : (e.target.value as MonthlyBillingStatus)
            )
          }
          className="focus:border-primary-500 focus:ring-primary-100 rounded-xl border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:outline-none"
        >
          <option value="all">
            {t('billing.invoices.allStatus', 'Tous les statuts')}
          </option>
          <option value={MonthlyBillingStatus.DRAFT}>
            {t('billing.invoices.draft', 'Brouillon')}
          </option>
          <option value={MonthlyBillingStatus.PENDING}>
            {t('billing.invoices.pending', 'En attente')}
          </option>
          <option value={MonthlyBillingStatus.PAID}>
            {t('billing.invoices.paid', 'Payée')}
          </option>
          <option value={MonthlyBillingStatus.FAILED}>
            {t('billing.invoices.failed', 'Échouée')}
          </option>
          <option value={MonthlyBillingStatus.CANCELLED}>
            {t('billing.invoices.cancelled', 'Annulée')}
          </option>
        </select>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary-600 h-8 w-8 animate-spin" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="border-red-200 bg-error-50 rounded-xl border p-6 text-center">
          <p className="text-error-700">
            {t('billing.invoices.error', 'Erreur lors du chargement')}
          </p>
        </div>
      )}

      {/* Invoices */}
      {!isLoading && !error && (
        <>
          {invoices.length === 0 ? (
            <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-8 text-center">
              <FileText className="mx-auto mb-3 h-12 w-12 text-neutral-400" />
              <p className="font-semibold text-neutral-900">
                {t('billing.invoices.empty', 'Aucune facture')}
              </p>
              <p className="mt-1 text-sm text-neutral-600">
                {t(
                  'billing.invoices.emptyDescription',
                  'Aucune facture ne correspond aux critères sélectionnés'
                )}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {invoices.map(invoice => (
                <div
                  key={invoice.uuid}
                  className="group hover:border-primary-200 rounded-xl border border-neutral-200 bg-white p-4 transition-all hover:shadow-md"
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex items-center gap-3">
                        <div className="bg-primary-50 flex h-10 w-10 items-center justify-center rounded-xl">
                          <FileText className="text-primary-600 h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="font-bold text-neutral-900">
                            {t('billing.invoices.invoice', 'Facture')}{' '}
                            {formatBillingDate(invoice.createdAt)}
                          </h3>
                          <p className="text-sm text-neutral-600">
                            {t(
                              'billing.invoices.monthYear',
                              'Mois {{month}}/{{year}}',
                              {
                                month: invoice.month,
                                year: invoice.year,
                              }
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="ml-13 grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
                        <div>
                          <p className="text-neutral-600">
                            {t('billing.invoices.folders', 'Dossiers')}
                          </p>
                          <p className="font-semibold text-neutral-900">
                            {invoice.uniqueFoldersCount}
                          </p>
                        </div>
                        <div>
                          <p className="text-neutral-600">
                            {t('billing.invoices.amount', 'Montant')}
                          </p>
                          <p className="font-bold text-neutral-900">
                            {formatAmount(invoice.totalAmountTTC)}
                          </p>
                        </div>
                        <div>
                          <p className="text-neutral-600">
                            {t('billing.invoices.status', 'Statut')}
                          </p>
                          <span
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${getBillingStatusColor(
                              invoice.status
                            )}`}
                          >
                            {translateBillingStatus(invoice.status)}
                          </span>
                        </div>
                      </div>

                      {invoice.paidAt && (
                        <div className="mt-2 flex items-center gap-1 text-xs text-neutral-500">
                          <Calendar className="h-3 w-3" />
                          {t('billing.invoices.paidAt', 'Payée le {{date}}', {
                            date: formatBillingDate(invoice.paidAt),
                          })}
                        </div>
                      )}
                    </div>

                    <div className="ml-4 flex flex-col gap-2">
                      {(invoice.stripeInvoicePdfUrl ||
                        invoice.stripeInvoiceUrl) && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleDownloadInvoice(invoice)}
                            className="whitespace-nowrap"
                          >
                            <Download className="mr-1 h-4 w-4" />
                            {t('billing.invoices.download', 'Télécharger')}
                          </Button>
                        )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onViewDetails(invoice.uuid)}
                        className="whitespace-nowrap"
                      >
                        {t('billing.invoices.details', 'Détails')}
                      </Button>
                      {invoice.stripeInvoiceUrl && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            window.open(
                              invoice.stripeInvoiceUrl || '',
                              '_blank'
                            )
                          }
                          className="whitespace-nowrap"
                        >
                          <ExternalLink className="mr-1 h-4 w-4" />
                          {t('billing.invoices.view', 'Voir')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 border-t border-neutral-200 pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                {t('common.previous', 'Précédent')}
              </Button>
              <span className="text-sm text-neutral-600">
                {t('common.page', 'Page {{current}}/{{total}}', {
                  current: pagination.currentPage,
                  total: pagination.totalPages,
                })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setPage(p => Math.min(pagination.totalPages, p + 1))
                }
                disabled={page === pagination.totalPages}
              >
                {t('common.next', 'Suivant')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
